import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { userApi, apiError } from "@/lib/userApi";
import { useUserAuth } from "@/context/UserAuthContext";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck, Tag } from "lucide-react";
import { CircuitBg } from "@/components/CircuitBg";
import { GlassField, GlassButton, GoogleIcon } from "@/components/GlassBits";
import { playForgeGate } from "@/components/ForgeGate";
import { pickCoverPhrase } from "@/lib/coverPhrases";

/** Acceso de usuarios (la comunidad y el editor). Los administradores entran en /admin. */
export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(null);
  const [phrase] = useState(pickCoverPhrase);
  const { signIn } = useUserAuth();
  const navigate = useNavigate();

  const doLogin = async () => {
    if (!email.trim() || !password) return toast.error("Escribe tu correo y contraseña");
    setBusy("login");
    try {
      const { data } = await userApi.post("/user/auth/login", { email: email.trim(), password });
      signIn(data.user);
      toast.success(`Hola ${data.user.name || data.user.email}`);
      await playForgeGate({
        title: `BIENVENIDO${data.user.name ? `, ${data.user.name.toUpperCase()}` : ""}`,
        subtitle: "A LA COMUNIDAD DE DIGITAL FORGE",
      });
      navigate("/muro");
    } catch (e) {
      toast.error(apiError(e, "No se pudo iniciar sesión"));
      if (e.response?.status === 403) navigate(`/registro?verificar=${encodeURIComponent(email.trim())}`);
    } finally { setBusy(null); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/muro";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const forgot = async () => {
    if (!email.trim()) return toast.error("Escribe tu correo primero");
    setBusy("forgot");
    try {
      await userApi.post("/user/auth/forgot-password", { email: email.trim() });
      toast.success("Si el correo existe, te mandamos un enlace para crear una contraseña nueva.");
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(null); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center circuit-bg theme-noir py-14" style={{ backgroundColor: "#05070C" }}>
      <CircuitBg />
      <div className="circuit-veil" aria-hidden="true" />
      <div className="w-full max-w-md px-6 animate-fade-up">
        <div className="flex flex-col items-center mb-7" style={{ textShadow: "0 2px 18px rgba(0,0,0,0.9)" }}>
          <img src="/logo-digital-forge.jpg" alt="Digital Forge" data-testid="brand-logo"
            className="w-56 h-56 sm:w-72 sm:h-72 lg:w-80 lg:h-80 rounded-full object-cover mx-auto mb-5 border border-white/10 brand-hue is-logo" />
          <div className="font-heading text-lg md:text-xl tracking-[0.42em] text-amber-300/90 mb-2.5">DIGITAL FORGE</div>
          <h1 className="cover-title brand-hue text-4xl sm:text-5xl lg:text-6xl" data-testid="cover-headline">
            {phrase}
          </h1>
          <p className="text-base md:text-lg text-white/80 mt-3 font-heading">Entra a tu cuenta</p>
        </div>

        <div className="glass-card forge-card p-7" data-testid="access-card">
          <div className="space-y-4">
            <GlassField data-testid="login-email-input" label="Correo" type="email" value={email}
              autoComplete="email" placeholder="tucorreo@ejemplo.com"
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doLogin()} />
            <GlassField data-testid="login-password-input" label="Contraseña" type="password" value={password}
              autoComplete="current-password" placeholder="••••••••"
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doLogin()} />
          </div>

          <div className="space-y-2.5 mt-6">
            <GlassButton data-testid="login-submit-btn" tone="red" loading={busy === "login"}
              onClick={doLogin}>Iniciar sesión <ArrowRight className="w-4 h-4" /></GlassButton>
            <GlassButton data-testid="login-google-btn" tone="neutral" onClick={googleLogin}>
              <GoogleIcon /> Continuar con Google
            </GlassButton>
            <GlassButton data-testid="login-forgot-btn" tone="ghost" loading={busy === "forgot"}
              onClick={forgot}>Olvidé mi contraseña</GlassButton>
          </div>

          <p className="text-center text-xs text-white/55 mt-5">
            ¿No tienes cuenta?{" "}
            <Link to="/registro" data-testid="go-register-link" className="text-red-400 hover:text-red-300 font-semibold">
              Crear cuenta
            </Link>
          </p>
        </div>

        <Link to="/precios" data-testid="go-pricing-link"
          className="mt-6 flex items-center justify-center gap-2 text-xs text-amber-300/80 hover:text-amber-200 transition-colors">
          <Tag className="w-3.5 h-3.5" /> Ver la lista de precios
        </Link>

        <Link to="/admin" data-testid="go-admin-access-link"
          className="mt-3 flex items-center justify-center gap-2 text-[11px] text-white/40 hover:text-red-300 transition-colors">
          <ShieldCheck className="w-3.5 h-3.5" /> Soy administrador
        </Link>
      </div>
    </div>
  );
}
